import { Component, model, input, effect, ChangeDetectorRef, inject, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ImageModule } from 'primeng/image';
import { DialogModule } from 'primeng/dialog';
import { MenuModule } from 'primeng/menu';
import { BlockScheduleFileModel } from '../../../../models/project.model';
import { OciService } from '../../../../services/oci.service';
import { FileControlValue, LineItemModel, ReferenceImageModel, parseLineItemAdditionalInfo } from '../../../../models/template.model';
import { SpeechService } from '../../../../services/speech.service';
import { ValidationService } from '../../../../services/validation.service';
import { parseValidationConfig, FileValidationConfig } from '../../../../models/validation-config.model';
import { environment } from '../../../../../environments/environment';
import { ProjecttemplateService } from '../../../../services/projecttemplate.service';
import { MediaFallbackDirective } from '../../../../shared/directives/media-fallback.directive';
import { isSupportedPhotoFile, SUPPORTED_PHOTO_ACCEPT } from '../../../../utils/image-file.utils';
import { downloadChecklistFileResponse } from '../../../../utils/checklist-file-download.utils';
import { ProjectPhotoUnifiedModel } from '../../../../models/projectphoto.model';
import { PhotoViewerComponent } from '../../../photos/photo-viewer/photo-viewer.component';

interface PicturePreview {
  file: File;
  thumbnail: string;
  isPlaceholder?: boolean;
}

@Component({
  selector: 'app-picture-control',
  standalone: true,
  imports: [CommonModule, ImageModule, DialogModule, MenuModule, MediaFallbackDirective, PhotoViewerComponent],
  template: `
    <div class="checklist-control-container">
      <div class="ctrl-container-hdr">
        <div class="pic-label-group">
          <label class="checklist-control-label">
            {{ lineItem().name }}
            @if (lineItem().isRequired) {
              <span class="required-indicator">*</span>
            }
          </label>
          @if (countHint()) {
            <span class="pic-count" [class.pic-count--limit]="atLimit()">{{ countHint() }}</span>
          } @else if (newPicturePreviews.length + existingPictures.length > 0) {
            <span class="pic-count">({{ newPicturePreviews.length + existingPictures.length }} Photos)</span>
          }
        </div>
        <button type="button" class="ctrl-speaker-btn" (click)="speakLabel()"
          [attr.aria-label]="'Read label: ' + lineItem().name">
          <i class="ri-volume-up-line"></i>
        </button>
      </div>

      <!-- Hidden camera input -->
      @if (referenceImages().length > 0) {
        <div class="reference-section">
          <div class="reference-title">Reference Images</div>
          <div class="picture-grid">
            @for (image of referenceImages(); track image.id) {
              <div class="pic-item">
                <div class="pic-preview pic-preview--clickable" (click)="previewReferenceImage(image)">
                  <img [src]="getReferenceImageThumbnail(image)" [appMediaFallback]="image.thumbnailPath || image.filePath" [alt]="image.fileName || 'Reference image'" />
                  <div class="pic-ref-badge">REF</div>
                </div>
              </div>
            }
          </div>
        </div>
      }

      <!-- Hidden camera input -->
      <input
        #cameraInput
        type="file"
        (change)="onFileSelect($event)"
        style="display: none"
        [attr.accept]="supportedPhotoAccept"
        capture="camera"
        [disabled]="disabled()"
        multiple
      />

      <!-- Photo grid — always rendered so the add placeholder is visible -->
      <div class="picture-grid">
        <!-- New Pictures (Not Yet Uploaded) -->
        @for (preview of newPicturePreviews; track $index) {
          <div class="pic-item">
            <div class="pic-preview">
              @if (preview.isPlaceholder) {
                <div class="pic-heic-placeholder">
                  <i class="ri-image-2-line"></i>
                  <span>HEIC — converts on save</span>
                </div>
              } @else {
                <img [src]="preview.thumbnail" [alt]="preview.file.name" />
              }
              @if (!disabled()) {
                <div class="pic-actions">
                  <button type="button" class="ctrl-icon-btn ctrl-icon-btn--danger"
                    (click)="removeNewPicture($index)" title="Remove">
                    <i class="ri-close-line"></i>
                  </button>
                </div>
              }
              <div class="pic-new-badge">NEW</div>
            </div>
          </div>
        }

        <!-- Existing Uploaded Pictures -->
        @for (picture of existingPictures; track picture.id) {
          <div class="pic-item">
            @if (picture.thumbnailUrl) {
              <div class="pic-preview pic-preview--clickable" (click)="previewPicture(picture)">
                <img [src]="picture.thumbnailUrl" [appMediaFallback]="picture.thumbnailPath" [appMediaFallbackAlternate]="picture.filePath" [alt]="picture.fileName || 'Photo'" />
                <div class="pic-actions pic-actions--hover" (click)="$event.stopPropagation()">
                  <button type="button" class="ctrl-icon-btn"
                    (click)="$event.stopPropagation(); downloadMenu.toggle($event)" title="Download">
                    <i class="ri-download-2-line"></i>
                  </button>
                  <p-menu #downloadMenu [model]="getDownloadMenuItems(picture)" [popup]="true" appendTo="body" [baseZIndex]="10000">
                    <ng-template pTemplate="item" let-item>
                      <a class="p-menuitem-link" (click)="$event.preventDefault(); $event.stopPropagation(); item.command(); downloadMenu.hide()">
                        <span [class]="'p-menuitem-icon ' + item.icon"></span>
                        <span class="p-menuitem-text">{{ item.label }}</span>
                      </a>
                    </ng-template>
                  </p-menu>
                  @if (!disabled()) {
                    <button type="button" class="ctrl-icon-btn ctrl-icon-btn--danger"
                      (click)="$event.stopPropagation(); deletePicture(picture)" title="Delete">
                      <i class="ri-delete-bin-line"></i>
                    </button>
                  }
                </div>
              </div>
            } @else {
              <div class="pic-preview">
                <div class="pic-spinner">
                  <i class="ri-loader-4-line ctrl-spin"></i>
                </div>
              </div>
            }
          </div>
        }

        <!-- Take Photo placeholder card (Figma: dashed border, secondary-50 bg) -->
        @if (!disabled()) {
          <button type="button" class="ctrl-photo-add" (click)="cameraInput.click()"
            [disabled]="atLimit()"
            [title]="atLimit() ? 'Maximum ' + maxCount() + ' photos allowed' : 'Opens camera on mobile devices, file picker on desktop'">
            <i class="ri-add-line"></i>Take Photo
          </button>
        }
      </div>
      @if (errors().length > 0) {
        @for (err of errors(); track $index) {
          <small class="p-error d-block">{{ err }}</small>
        }
      } @else if (unsupportedFileMessage) {
        <small class="p-error d-block">{{ unsupportedFileMessage }}</small>
      } @else if (hint()) {
        <small class="text-color-secondary">{{ hint() }}</small>
      }
    </div>

    <!-- Preview Dialog -->
    <p-dialog [(visible)]="showPreviewDialog" [modal]="true" [style]="{ width: '90vw' }" [header]="previewPictureName" [dismissableMask]="true">
      <div class="pic-preview-dialog-body">
        @if (previewImageUrl) {
          <p-image [src]="previewImageUrl" [alt]="previewPictureName" [preview]="true">
            <ng-template pTemplate="image">
              <img [src]="previewImageUrl" [appMediaFallback]="previewImageFallbackPath" [alt]="previewPictureName" style="max-width: 100%; max-height: 70vh; object-fit: contain;" />
            </ng-template>
          </p-image>
        } @else {
          <i class="ri-loader-4-line ctrl-spin" style="font-size: 2.5rem; color: var(--color-primary-500);"></i>
        }
      </div>
    </p-dialog>

    <p-dialog [(visible)]="showAprysePreviewDialog" [modal]="true" [style]="{ width: '95vw', height: '90vh' }"
      [contentStyle]="{ height: 'calc(90vh - 4rem)', padding: '0' }" [header]="aprysePreviewPhoto?.photoName || 'Photo'"
      [dismissableMask]="true" [closable]="true" (onHide)="closeAprysePreview()">
      @if (aprysePreviewPhoto) {
        <app-photo-viewer [embedded]="true" [readOnly]="true" [photo]="aprysePreviewPhoto"
          [embeddedPhotos]="getAprysePreviewPhotos()" [photoProjectId]="projectId()"
          (photoChanged)="onAprysePreviewPhotoChanged($event)" />
      }
    </p-dialog>

    <p-dialog [(visible)]="showDeletePictureDialog" [modal]="true" [closable]="true" [draggable]="false" [resizable]="false"
      [style]="{ width: 'min(560px, calc(100vw - 32px))' }" styleClass="kansei-dialog photo-delete-dialog"
      header="Delete Photo" (onHide)="cancelPictureDeletion()">
      <div class="unsaved-body">
        <div class="unsaved-icon" aria-hidden="true">
          <i class="ri-error-warning-line"></i>
        </div>
        <div class="unsaved-copy">
          <p class="unsaved-title">Delete this photo permanently?</p>
          <p class="unsaved-message">{{ pendingDeletePicture?.fileName || 'This photo' }} will be permanently deleted and cannot be recovered.</p>
        </div>
      </div>

      <div class="modal-footer unsaved-actions">
        <button type="button" class="modal-btn-cancel unsaved-stay-btn" (click)="cancelPictureDeletion()">Cancel</button>
        <button type="button" class="modal-btn-primary" (click)="confirmPictureDeletion()">Delete</button>
      </div>
    </p-dialog>
  `,
  styles: [`
    .checklist-control-container {
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 14px 16px;
      background: var(--color-white);
    }
    .pic-label-group {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
    .pic-count {
      font-size: 14px;
      color: var(--color-grey-500);
    }
    .pic-count--limit {
      color: #EB4747;
      font-weight: 600;
    }
    .picture-grid {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
    }
    .reference-section {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    .reference-title {
      font-size: 14px;
      font-weight: var(--font-weight-semibold);
      color: var(--color-text-secondary);
    }
    .pic-item {
      border-radius: 4px;
      overflow: hidden;
      background: var(--color-white);
      width: 203px;
      height: 139px;
      flex-shrink: 0;
    }
    .pic-preview {
      position: relative;
      width: 203px;
      height: 139px;
      background: var(--color-bg-surface-muted);
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
    }
    .pic-preview img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    .pic-preview--clickable {
      cursor: pointer;
    }
    :host ::ng-deep .p-dialog .p-dialog-content:has(app-photo-viewer) {
      overflow: hidden;
    }
    .pic-spinner {
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 2rem;
      color: var(--color-grey-400);
    }
    .pic-actions {
      position: absolute;
      top: 8px;
      right: 8px;
      display: flex;
      gap: 4px;
      z-index: 10;
    }
    .pic-preview--clickable .pic-actions--hover {
      opacity: 0;
      transition: opacity var(--motion-fast);
      pointer-events: none;
    }
    .pic-preview--clickable:hover .pic-actions--hover {
      opacity: 1;
      pointer-events: all;
    }
    .pic-new-badge {
      position: absolute;
      top: 8px;
      left: 8px;
      background: var(--color-success-mid);
      color: var(--color-white);
      font-size: 10px;
      font-weight: var(--font-weight-semibold);
      padding: 2px 6px;
      border-radius: 4px;
      z-index: 5;
    }
    .pic-ref-badge {
      position: absolute;
      top: 8px;
      left: 8px;
      background: var(--color-primary-500);
      color: var(--color-white);
      font-size: 10px;
      font-weight: var(--font-weight-semibold);
      padding: 2px 6px;
      border-radius: 4px;
      z-index: 5;
    }
    .pic-heic-placeholder {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 4px;
      width: 100%;
      height: 100%;
      color: var(--color-grey-500);
    }
    .pic-heic-placeholder i {
      font-size: 1.75rem;
      color: var(--color-grey-400);
    }
    .pic-heic-placeholder span {
      font-size: 10px;
      text-align: center;
      padding: 0 8px;
    }
    .pic-preview-dialog-body {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 400px;
    }
    /* Download menu styling */
    ::ng-deep .p-menu .p-menuitem-link {
      padding: 0.75rem 1rem !important;
      display: flex !important;
      align-items: center !important;
      gap: 0.75rem !important;
      cursor: pointer !important;
    }
    ::ng-deep .p-menu .p-menuitem-link:hover {
      background-color: var(--color-bg-surface-muted) !important;
    }
    ::ng-deep .p-menu .p-menuitem-icon {
      font-size: 1rem !important;
      color: var(--color-primary-500) !important;
    }
    ::ng-deep .p-menu .p-menuitem-text {
      font-size: 0.9rem !important;
      color: var(--color-text-primary) !important;
    }
    ::ng-deep .p-menu .p-menuitem {
      border-bottom: 1px solid var(--color-border-default);
    }
    ::ng-deep .p-menu .p-menuitem:last-child {
      border-bottom: none;
    }
    ::ng-deep .p-menu {
      min-width: 12rem !important;
      padding: 0.5rem 0 !important;
    }
    :host ::ng-deep .photo-delete-dialog .p-dialog-content {
      padding: 0 !important;
    }
    :host ::ng-deep .photo-delete-dialog .p-dialog-header {
      background: linear-gradient(135deg, #062847 0%, #0c3b62 100%) !important;
      border-bottom: 0 !important;
      color: #fff !important;
      padding: 18px 20px !important;
    }
    :host ::ng-deep .photo-delete-dialog .p-dialog-title {
      color: #fff !important;
      font-size: 20px !important;
      font-weight: 700 !important;
    }
    :host ::ng-deep .photo-delete-dialog .p-dialog-header-icon,
    :host ::ng-deep .photo-delete-dialog .p-dialog-close-button {
      color: rgba(255, 255, 255, 0.8) !important;
    }
    .unsaved-body {
      align-items: flex-start;
      display: grid;
      gap: 14px;
      grid-template-columns: 42px 1fr;
      padding: 22px 24px 20px;
    }
    .unsaved-icon {
      align-items: center;
      background: #fff3cd;
      border: 1px solid #ffe39a;
      border-radius: 50%;
      color: #a66600;
      display: inline-flex;
      font-size: 22px;
      height: 42px;
      justify-content: center;
      width: 42px;
    }
    .unsaved-copy { min-width: 0; }
    .unsaved-title {
      color: var(--color-text-primary);
      font-size: 16px;
      font-weight: 700;
      line-height: 1.35;
      margin: 0 0 6px;
    }
    .unsaved-message {
      color: var(--color-text-secondary);
      font-size: 14px;
      line-height: 1.5;
      margin: 0;
    }
    .unsaved-actions { padding: 16px 24px !important; }
    .unsaved-actions button {
      height: 42px;
      font-size: 14px;
      padding-left: 18px;
      padding-right: 18px;
      white-space: nowrap;
    }
    .unsaved-stay-btn {
      background: var(--color-bg-surface-muted);
      border-color: transparent;
      min-width: 78px;
    }
    @media (max-width: 560px) {
      .unsaved-body { grid-template-columns: 1fr; }
      .unsaved-actions { align-items: stretch; flex-direction: column-reverse; }
      .unsaved-actions button { width: 100%; }
    }
  `]
})
export class PictureControlComponent {
  readonly supportedPhotoAccept = SUPPORTED_PHOTO_ACCEPT;
  lineItem = input.required<LineItemModel>();
  value = model<FileControlValue>({ newFiles: [], existingFileIds: [] });
  disabled = input<boolean>(false);
  showError = input<boolean>(false);
  // The template designer/print preview uses this control without a project.
  // Only persisted checklist photos with a project open in the Apryse viewer.
  projectId = input<number>(0);
  existingFilesData = input<BlockScheduleFileModel[]>([]);
  referenceImages = computed<ReferenceImageModel[]>(() =>
    parseLineItemAdditionalInfo(this.lineItem().additionalInfo).referenceImages?.filter(image => !!image?.filePath) ?? []
  );

  private validationService = inject(ValidationService);

  /** Configured max photo count — Infinity when not set */
  maxCount = computed(() => {
    const cfg = parseValidationConfig<FileValidationConfig>(this.lineItem().validationConfig);
    return cfg?.maxCount ?? Infinity;
  });

  /** Total photos currently held (new + existing) */
  totalCount = computed(() =>
    (this.value()?.newFiles?.length ?? 0) + (this.value()?.existingFileIds?.length ?? 0)
  );

  /** True when the user has reached or exceeded the configured limit */
  atLimit = computed(() => isFinite(this.maxCount()) && this.totalCount() >= this.maxCount());

  /** Remaining slots before the limit is hit */
  remainingSlots = computed(() =>
    isFinite(this.maxCount()) ? Math.max(0, this.maxCount() - this.totalCount()) : Infinity
  );

  errors = computed(() => {
    if (!this.showError()) return [];
    return this.validationService.validate(this.lineItem(), this.value());
  });

  countHint = computed(() => {
    if (!isFinite(this.maxCount())) return '';
    if (this.atLimit()) return `${this.totalCount()}/${this.maxCount()} photos — limit reached`;
    return `${this.totalCount()}/${this.maxCount()} photos`;
  });

  hint = computed(() => this.errors().length > 0 ? '' : this.validationService.getHint(this.lineItem()));

  newPicturePreviews: PicturePreview[] = [];
  existingPictures: BlockScheduleFileModel[] = [];
  unsupportedFileMessage = '';

  // Preview dialog properties
  showPreviewDialog = false;
  showAprysePreviewDialog = false;
  previewImageUrl: string | null = null;
  previewImageFallbackPath: string | null = null;
  previewPictureName: string = '';
  aprysePreviewPhoto: ProjectPhotoUnifiedModel | null = null;
  showDeletePictureDialog = false;
  pendingDeletePicture: BlockScheduleFileModel | null = null;

  constructor(
    private ociService: OciService,
    private cdr: ChangeDetectorRef,
    private projecttemplateService: ProjecttemplateService
  ) {
    // Sync value model to local arrays and generate previews
    effect(() => {
      const val = this.value();
      if (val && val.newFiles && Array.isArray(val.newFiles)) {
        // Filter to ensure we only have actual File objects
        const validFiles = val.newFiles.filter(f => f instanceof File);
        if (validFiles.length > 0) {
          this.generatePreviews(validFiles);
        } else if (val.newFiles.length === 0) {
          // Clear previews if no files
          this.newPicturePreviews = [];
          this.cdr.markForCheck();
        }
      } else if (val && (!val.newFiles || val.newFiles.length === 0)) {
        // Clear previews if no files
        this.newPicturePreviews = [];
        this.cdr.markForCheck();
      }
    });

    // Sync existing files from input and load thumbnails
    effect(() => {
      const files = this.existingFilesData() || [];
      // Create a shallow copy to avoid mutation issues
      this.existingPictures = [...files];

      // Set CDN URLs for existing pictures
      this.existingPictures.forEach((picture, index) => {
        if (picture.thumbnailPath && !picture.thumbnailUrl) {
          // Use CDN URL directly instead of fetching blob
          this.existingPictures[index] = {
            ...picture,
            thumbnailUrl: `${environment.cdnBaseUrl}?key=${encodeURIComponent(picture.thumbnailPath)}`
          };
        }
      });
      this.cdr.markForCheck();
    });
  }

  private isHeicFile(file: File): boolean {
    const ext = file.name.split('.').pop()?.toLowerCase();
    return ext === 'heic' || ext === 'heif' || file.type === 'image/heic' || file.type === 'image/heif';
  }

  private generatePreviews(files: File[]): void {
    const previews: PicturePreview[] = [];
    let processed = 0;

    if (files.length === 0) {
      this.newPicturePreviews = [];
      this.cdr.markForCheck();
      return;
    }

    files.forEach((file, index) => {
      // Double-check that we have a valid File object
      if (!(file instanceof File)) {
        console.warn('Invalid file object at index', index, file);
        processed++;
        if (processed === files.length) {
          this.newPicturePreviews = previews.filter(p => p !== undefined);
          this.cdr.markForCheck();
        }
        return;
      }

      // HEIC/HEIF cannot be previewed in Chrome/Firefox/Edge — show placeholder
      if (this.isHeicFile(file)) {
        previews[index] = { file, thumbnail: '', isPlaceholder: true };
        processed++;
        if (processed === files.length) {
          this.newPicturePreviews = previews.filter(p => p !== undefined);
          this.cdr.markForCheck();
        }
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        previews[index] = {
          file: file,
          thumbnail: e.target?.result as string
        };
        processed++;
        if (processed === files.length) {
          this.newPicturePreviews = previews.filter(p => p !== undefined);
          this.cdr.markForCheck();
        }
      };
      reader.onerror = (error) => {
        console.error('Error reading file:', file.name, error);
        previews[index] = { file, thumbnail: '', isPlaceholder: true };
        processed++;
        if (processed === files.length) {
          this.newPicturePreviews = previews.filter(p => p !== undefined);
          this.cdr.markForCheck();
        }
      };
      reader.readAsDataURL(file);
    });
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const currentValue = this.value() || { newFiles: [], existingFileIds: [] };
      const slots = this.remainingSlots();
      const selectedFiles = Array.from(input.files);
      const supportedFiles = selectedFiles.filter(isSupportedPhotoFile);
      const rejectedCount = selectedFiles.length - supportedFiles.length;
      this.unsupportedFileMessage = rejectedCount > 0
        ? `${rejectedCount} unsupported image file(s) were skipped.`
        : '';
      const newFilesList = supportedFiles.slice(0, isFinite(slots) ? slots : undefined);

      if (newFilesList.length > 0) {
        this.value.set({
          newFiles: [...(currentValue.newFiles || []), ...newFilesList],
          existingFileIds: currentValue.existingFileIds || []
        });
      }

      input.value = '';
    }
  }

  removeNewPicture(index: number): void {
    const currentValue = this.value() || { newFiles: [], existingFileIds: [] };
    const updatedNewFiles = [...(currentValue.newFiles || [])];
    updatedNewFiles.splice(index, 1);

    this.value.set({
      newFiles: updatedNewFiles,
      existingFileIds: currentValue.existingFileIds || []
    });

    this.cdr.markForCheck();
  }

  deletePicture(picture: BlockScheduleFileModel): void {
    if (this.disabled()) {
      return;
    }

    this.pendingDeletePicture = picture;
    this.showDeletePictureDialog = true;
  }

  cancelPictureDeletion(): void {
    this.showDeletePictureDialog = false;
    this.pendingDeletePicture = null;
  }

  confirmPictureDeletion(): void {
    const picture = this.pendingDeletePicture;
    this.cancelPictureDeletion();
    if (picture) {
      this.deletePictureConfirmed(picture);
    }
  }

  private deletePictureConfirmed(picture: BlockScheduleFileModel): void {
    // Optimistically remove from UI
    this.existingPictures = this.existingPictures.filter(p => p.id !== picture.id);

    const currentValue = this.value() || { newFiles: [], existingFileIds: [] };
    this.value.set({
      newFiles: currentValue.newFiles || [],
      existingFileIds: (currentValue.existingFileIds || []).filter(id => id !== picture.id)
    });

    this.cdr.markForCheck();

    // Delete from backend
    this.projecttemplateService.deleteFile(picture.id).subscribe({
      error: () => {
        // Restore picture on failure
        this.existingPictures = [...this.existingPictures, picture];
        const restoredValue = this.value() || { newFiles: [], existingFileIds: [] };
        this.value.set({
          newFiles: restoredValue.newFiles || [],
          existingFileIds: [...(restoredValue.existingFileIds || []), picture.id]
        });
        this.cdr.markForCheck();
      }
    });
  }

  getDownloadMenuItems(picture: BlockScheduleFileModel) {
    return [
      {
        label: 'Original File',
        icon: 'pi pi-file',
        command: () => this.downloadOriginalFile(picture)
      },
      {
        label: 'Optimized File',
        icon: 'pi pi-image',
        command: () => this.downloadOptimizedFile(picture)
      }
    ];
  }

  downloadOriginalFile(picture: BlockScheduleFileModel): void {
    this.projecttemplateService.downloadChecklistAttachment(picture.id).subscribe({
      next: response => downloadChecklistFileResponse(response, picture.fileName || 'photo-original'),
      error: (err) => {
        console.error('Error downloading original file:', err);
      }
    });
  }

  downloadOptimizedFile(picture: BlockScheduleFileModel): void {
    this.projecttemplateService.downloadChecklistAttachment(picture.id, true).subscribe({
      next: response => downloadChecklistFileResponse(response, picture.fileName || 'photo'),
      error: (err) => {
        console.error('Error downloading optimized file:', err);
      }
    });
  }

  previewPicture(picture: BlockScheduleFileModel): void {
    if (this.projectId() <= 0) {
      this.previewPictureName = picture.fileName || 'Photo';
      this.previewImageFallbackPath = picture.filePath || picture.thumbnailPath || null;
      this.previewImageUrl = picture.imageUrl || picture.thumbnailUrl || null;
      this.showPreviewDialog = true;
      return;
    }

    const photo = this.getAprysePreviewPhotos().find(item => item.photoId === picture.id);
    if (!photo) {
      return;
    }

    this.aprysePreviewPhoto = photo;
    this.showAprysePreviewDialog = true;
  }

  getAprysePreviewPhotos(): ProjectPhotoUnifiedModel[] {
    return this.existingPictures
      .filter(picture => !!picture.filePath)
      .map(picture => ({
        photoId: picture.id,
        photoPath: picture.filePath,
        thumbnailPath: picture.thumbnailPath,
        photoName: picture.fileName || 'Photo',
        createdDate: picture.createdDate ? new Date(picture.createdDate) : undefined,
        createdBy: picture.createdBy,
        sourceType: 'Skid',
        blockScheduleId: picture.blockScheduleId
      }));
  }

  onAprysePreviewPhotoChanged(photo: ProjectPhotoUnifiedModel): void {
    this.aprysePreviewPhoto = photo;
  }

  closeAprysePreview(): void {
    this.showAprysePreviewDialog = false;
    this.aprysePreviewPhoto = null;
  }

  previewReferenceImage(image: ReferenceImageModel): void {
    this.previewPictureName = image.fileName || 'Reference Image';
    this.previewImageFallbackPath = image.filePath || image.thumbnailPath || null;
    this.previewImageUrl = this.getReferenceImageFull(image);
    this.showPreviewDialog = true;
  }

  getReferenceImageThumbnail(image: ReferenceImageModel): string {
    const path = image.thumbnailPath || image.filePath;
    return `${environment.cdnBaseUrl}?key=${encodeURIComponent(path)}`;
  }

  private getReferenceImageFull(image: ReferenceImageModel): string {
    return `${environment.cdnBaseUrl}?key=${encodeURIComponent(image.filePath)}`;
  }

  loadFullImage(picture: BlockScheduleFileModel): void {
    if (!picture.filePath) return;
    if (picture.imageUrl) return; // Already loaded

    // Use CDN URL for full image
    picture.imageUrl = `${environment.cdnBaseUrl}?key=${encodeURIComponent(picture.filePath)}`;

    // Update preview dialog if it's currently showing this picture
    if (this.showPreviewDialog && this.previewPictureName === picture.fileName) {
      this.previewImageFallbackPath = picture.filePath || picture.thumbnailPath || null;
      this.previewImageUrl = picture.imageUrl ?? null;
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  private speechService = inject(SpeechService);

  speakLabel(): void {
    this.speechService.speak(this.lineItem().name);
  }
}
